/**
 * Core tracer implementation for W3C Trace Context propagation
 *
 * Generates trace and span IDs compliant with W3C Trace Context specification:
 * https://www.w3.org/TR/trace-context/
 */

import {getLoggerCallback, telemetryConfig} from './config';
import {TraceContext, TraceFlags} from './types';

/**
 * Generate a random hex string of specified length
 * Uses crypto.randomUUID() when available, falls back to Math.random()
 */
function generateRandomHex(length: number): string {
  try {
    // Try using crypto.randomUUID() (available in React Native 0.76+)
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      // Generate UUID and extract hex characters
      const uuid = crypto.randomUUID();
      const hex = uuid.replace(/-/g, '');

      // If we need more characters, generate multiple UUIDs
      if (hex.length < length) {
        let result = hex;
        while (result.length < length) {
          result += crypto.randomUUID().replace(/-/g, '');
        }
        return result.slice(0, length);
      }

      return hex.slice(0, length);
    }
  } catch (_error) {
    if (telemetryConfig.debug) {
      const callback = getLoggerCallback();
      if (callback) {
        callback('OTEL Tracer', 'crypto.randomUUID not available, using fallback', {
          timestamp: Date.now(),
          level: 'debug',
        });
      }
    }
  }

  // Fallback to Math.random() for older environments
  let result = '';
  const hexChars = '0123456789abcdef';

  for (let i = 0; i < length; i++) {
    result += hexChars[Math.floor(Math.random() * 16)];
  }

  return result;
}

/**
 * LiquorStoreTracer
 *
 * Manages trace context lifecycle for distributed tracing.
 * Generates W3C-compliant trace and span IDs.
 */
export class LiquorStoreTracer {
  private currentTrace: TraceContext | null = null;

  /**
   * Generate a W3C-compliant trace ID (32 hex characters)
   * Format: 32 lowercase hex digits
   * Example: "4bf92f3577b34da6a3ce929d0e0e4736"
   */
  generateTraceId(): string {
    return generateRandomHex(32);
  }

  /**
   * Generate a W3C-compliant span ID (16 hex characters)
   * Format: 16 lowercase hex digits
   * Example: "00f067aa0ba902b7"
   */
  generateSpanId(): string {
    return generateRandomHex(16);
  }

  /**
   * Determine if trace should be sampled based on configured sample rate
   * Returns SAMPLED or NONE flag
   */
  shouldSample(): TraceFlags {
    if (!telemetryConfig.enabled) {
      return TraceFlags.NONE;
    }

    // Always sample if rate is 1.0
    if (telemetryConfig.sampleRate >= 1.0) {
      return TraceFlags.SAMPLED;
    }

    // Never sample if rate is 0.0
    if (telemetryConfig.sampleRate <= 0.0) {
      return TraceFlags.NONE;
    }

    // Probabilistic sampling
    return Math.random() < telemetryConfig.sampleRate ? TraceFlags.SAMPLED : TraceFlags.NONE;
  }

  /**
   * Start a new root trace
   *
   * @param operationName - Human-readable name for the operation
   * @param parentSpanId - Optional parent span ID for nested traces
   * @returns The created trace context
   */
  startTrace(operationName: string, parentSpanId?: string): TraceContext {
    const traceId = this.generateTraceId();
    const spanId = this.generateSpanId();
    const traceFlags = this.shouldSample();

    this.currentTrace = {
      traceId,
      spanId,
      traceFlags,
      operationName,
      startTime: Date.now(),
      parentSpanId,
    };

    if (telemetryConfig.debug) {
      const callback = getLoggerCallback();
      if (callback) {
        callback('OTEL Tracer', 'Started trace', {
          timestamp: Date.now(),
          level: 'debug',
          context: {
            operationName,
            traceId,
            spanId,
            traceFlags,
            parentSpanId,
          },
        });
      }
    }

    return this.currentTrace;
  }

  /**
   * Get the current active trace context
   */
  getCurrentTrace(): TraceContext | null {
    return this.currentTrace;
  }

  /**
   * End the current trace
   */
  endTrace(): void {
    if (this.currentTrace && telemetryConfig.debug) {
      const duration = Date.now() - this.currentTrace.startTime;
      const callback = getLoggerCallback();
      if (callback) {
        callback('OTEL Tracer', 'Ended trace', {
          timestamp: Date.now(),
          level: 'debug',
          context: {
            operationName: this.currentTrace.operationName,
            traceId: this.currentTrace.traceId,
            duration: `${duration}ms`,
          },
        });
      }
    }

    this.currentTrace = null;
  }

  /**
   * Format the current trace as a W3C traceparent header value
   *
   * Format: version-trace_id-span_id-trace_flags
   * Example: "00-4bf92f3577b34da6a3ce929d0e0e4736-00f067aa0ba902b7-01"
   *
   * @returns Formatted traceparent header value, or null if no active trace
   */
  getTraceparent(): string | null {
    if (!this.currentTrace) {
      return null;
    }

    const {traceId, spanId, traceFlags} = this.currentTrace;

    // W3C Trace Context version is "00"
    const version = '00';

    // Format flags as 2-digit hex
    const flagsHex = traceFlags.toString(16).padStart(2, '0');

    return `${version}-${traceId}-${spanId}-${flagsHex}`;
  }

  /**
   * Get the tracestate header value (optional vendor-specific data)
   * Currently returns null as we don't use tracestate yet
   */
  getTracestate(): string | null {
    return this.currentTrace?.traceState || null;
  }

  /**
   * Check if telemetry is enabled and initialized
   */
  isEnabled(): boolean {
    return telemetryConfig.enabled;
  }

  /**
   * Reset the tracer state (useful for testing)
   */
  reset(): void {
    this.currentTrace = null;
  }
}

/**
 * Singleton tracer instance for the application
 */
export const tracer = new LiquorStoreTracer();
